import Badge from '@cloudscape-design/components/badge'
import Box from '@cloudscape-design/components/box'
import ButtonDropdown from '@cloudscape-design/components/button-dropdown'
import ColumnLayout from '@cloudscape-design/components/column-layout'
import SpaceBetween from '@cloudscape-design/components/space-between'
import StatusIndicator from '@cloudscape-design/components/status-indicator'
import type { Provider } from '@/lib/types'
import { computeFitScore, computeTemperature, mapBadgeColor } from '@/pages/clients/ClientProviders/ProviderDetailPanel'
import styles from '@/pages/clients/ClientProviders/DirectoryTab.module.css'

interface ProviderDirectoryCardProps {
  provider: Provider
  onGenerateEmail: (provider: Provider) => void
  onMarkAs: (provider: Provider, status: string) => void
  onRemove: (provider: Provider) => void
}

const TEMP_COLOR_MAP = { hot: 'red', warm: 'severity-medium', cold: 'grey' } as const

export default function ProviderDirectoryCard({
  provider,
  onGenerateEmail,
  onMarkAs,
  onRemove,
}: ProviderDirectoryCardProps) {
  const fit = computeFitScore(provider)
  const temp = computeTemperature(provider.engagement_signals)
  const cmsVol = provider.prescribing_volume_annual_usd
    ? `$${(provider.prescribing_volume_annual_usd / 1000).toFixed(0)}k`
    : '—'

  return (
    <SpaceBetween size="s">
      <ColumnLayout columns={2} borders="none">
        <SpaceBetween size="xxs">
          <Box variant="awsui-key-label">Specialty</Box>
          <Box>{provider.specialty}</Box>
        </SpaceBetween>
        <SpaceBetween size="xxs">
          <Box variant="awsui-key-label">Status</Box>
          <StatusIndicator type={provider.status === 'contacted' ? 'success' : 'info'}>
            {provider.status}
          </StatusIndicator>
        </SpaceBetween>
        <SpaceBetween size="xxs">
          <Box variant="awsui-key-label">Location</Box>
          <Box>{provider.city}, {provider.state}</Box>
        </SpaceBetween>
        <SpaceBetween size="xxs">
          <Box variant="awsui-key-label">CMS Volume</Box>
          <Box>{cmsVol}</Box>
        </SpaceBetween>
      </ColumnLayout>

      <SpaceBetween direction="horizontal" size="xs">
        <div className={styles.badgeRow}>
          <Badge color={mapBadgeColor(fit.color)}>{fit.label}</Badge>
          <Badge color={TEMP_COLOR_MAP[temp]}>{temp}</Badge>
        </div>
        <Box float="right">
          <ButtonDropdown
            variant="icon"
            ariaLabel="Provider actions"
            items={[
              { id: 'generate', text: 'Generate Email' },
              {
                id: 'mark-as',
                text: 'Mark as',
                items: [
                  { id: 'contacted', text: 'Contacted' },
                  { id: 'engaged', text: 'Engaged' },
                  { id: 'referred', text: 'Referred' },
                  { id: 'inactive', text: 'Inactive' },
                ],
              },
              { id: 'remove', text: 'Remove', disabled: false },
            ]}
            onItemClick={({ detail }) => {
              if (detail.id === 'generate') onGenerateEmail(provider)
              else if (detail.id === 'remove') onRemove(provider)
              else onMarkAs(provider, detail.id)
            }}
          />
        </Box>
      </SpaceBetween>
    </SpaceBetween>
  )
}
